import { ApiError } from '@/lib/api';

/**
 * Client-side reading of `POST /organizations/:id/runloop-provider/provision`.
 *
 * The wizard fires that endpoint automatically, so its failures are the only
 * thing standing between a new organization and a working sandbox. Every
 * response therefore has to resolve into one of three user-facing outcomes:
 *
 * - `in-progress` — another tab, replica, or a reload of this very wizard
 *   already holds the provisioning lease. Nothing is wrong; the wizard keeps
 *   showing "setting up" and polls the sandbox provider until the key lands.
 * - `failed` + `retryable` — a transient fault (Runloop unavailable, request
 *   timed out, server error). Offering "Try again" is honest.
 * - `failed` — a terminal fault (provisioning disabled, no service
 *   credential, unverified email, Runloop refused). Retrying the same
 *   request cannot help, so the wizard only offers the manual-key fallback.
 *
 * The server already writes actionable copy into `message`; we prefer it and
 * only fall back to generic text when a response carries none (network
 * failures, proxies, unexpected shapes).
 */
export type RunloopProvisioningOutcome =
  | { kind: 'in-progress'; message: string }
  | { kind: 'failed'; message: string; retryable: boolean };

/**
 * Provisioning is already running under the shared lease. Both codes mean the
 * same thing to a user: the org's Runloop binding is being mutated right now.
 * `RUNLOOP_PROVISION_IN_PROGRESS` comes from the provision endpoint,
 * `RUNLOOP_SETUP_IN_PROGRESS` from the key-write endpoints that share the
 * per-organization bind lease.
 */
const IN_PROGRESS_CODES = new Set(['RUNLOOP_PROVISION_IN_PROGRESS', 'RUNLOOP_SETUP_IN_PROGRESS']);

/**
 * Terminal failures: the same request will keep failing until an operator or
 * the user changes something, so the wizard hides "Try again" and points at
 * the manual key instead.
 */
const TERMINAL_CODES = new Set([
  'RUNLOOP_ACCOUNT_PROVISIONING_DISABLED',
  'RUNLOOP_PROVISION_NOT_CONFIGURED',
  'RUNLOOP_PROVISION_MANUAL_REQUIRED',
  'RUNLOOP_PROVISION_EXISTING_EMAIL',
  'RUNLOOP_PROVISION_REJECTED',
  'RUNLOOP_KEY_ALREADY_CONFIGURED',
]);

/** Transient failures worth a one-click retry. */
const RETRYABLE_CODES = new Set(['RUNLOOP_PROVISION_UNAVAILABLE']);

const GENERIC_FAILURE_MESSAGE =
  'Reflex could not finish setting up your sandbox account. Try again in a moment.';

// The transport's own copy ("Request timed out") is too bare for onboarding,
// and it understates what happened: the setup may well still be running
// server-side, in which case a retry picks up the same operation.
const TIMEOUT_MESSAGE = 'Setting up your sandbox took longer than expected. Try again in a moment.';

const IN_PROGRESS_MESSAGE =
  'Sandbox setup is already running for this organization. Waiting for it to finish.';

function serverMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const message = err.message.trim();
  // The transport falls back to `Request failed: <status>` when a response
  // carries no message. That is not something to show a user mid-onboarding.
  if (!message || /^Request failed: \d+$/.test(message)) return null;
  return message;
}

export function interpretRunloopProvisioningError(err: unknown): RunloopProvisioningOutcome {
  const code = err instanceof ApiError ? err.code : undefined;
  if (code && IN_PROGRESS_CODES.has(code)) {
    return { kind: 'in-progress', message: serverMessage(err) ?? IN_PROGRESS_MESSAGE };
  }
  if (code === 'TIMEOUT') {
    return { kind: 'failed', message: TIMEOUT_MESSAGE, retryable: true };
  }
  const message = serverMessage(err) ?? GENERIC_FAILURE_MESSAGE;
  if (code && TERMINAL_CODES.has(code)) {
    return { kind: 'failed', message, retryable: false };
  }
  if (code && RETRYABLE_CODES.has(code)) {
    return { kind: 'failed', message, retryable: true };
  }
  // Unknown ApiError: a 4xx is a request the client cannot fix by repeating
  // it, while a 5xx or a transport-level failure (status 0) can clear on its
  // own. Anything that is not an ApiError at all reached us from fetch, so it
  // is a transport problem too.
  if (err instanceof ApiError) {
    return { kind: 'failed', message, retryable: err.status === 0 || err.status >= 500 };
  }
  return { kind: 'failed', message, retryable: true };
}
